import React from 'react';
import useThemeCanvas, { pulseRgb } from '../useThemeCanvas';

const DROP_COUNT = 240;

export default function RainTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.drops = Array.from({ length: DROP_COUNT }, () => {
        const depth = Math.random(); // 0 = far (short, slow, faint), 1 = near
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          depth,
          len: 8 + depth * 22,
          speed: 3 + depth * 8,
          windBase: -0.3 + Math.random() * 0.1,
        };
      });
      state.splashes = [];
      state.flash = 0;
      state.nextFlash = null;
    },
    draw: (ctx, w, h, state, t) => {
      // Stormy sky backdrop (replaces a hard clear).
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        sky.addColorStop(0, '#070b14');
        sky.addColorStop(1, '#131b2e');
      } else {
        sky.addColorStop(0, '#cfd6e2');
        sky.addColorStop(1, '#e6eaf1');
      }
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);

      // Occasional distant lightning: brief soft sky illumination with a flicker.
      if (state.nextFlash == null) state.nextFlash = t + 8 + Math.random() * 18;
      if (t >= state.nextFlash) {
        state.flash = 1;
        state.nextFlash = t + 12 + Math.random() * 24;
      }
      if (state.flash > 0.02) {
        const flick = state.flash * (0.6 + 0.4 * Math.abs(Math.sin(t * 40)));
        const fg = ctx.createLinearGradient(0, 0, 0, h);
        fg.addColorStop(0, `rgba(226,232,240,${flick * (dark ? 0.16 : 0.08)})`);
        fg.addColorStop(1, 'rgba(226,232,240,0)');
        ctx.fillStyle = fg;
        ctx.fillRect(0, 0, w, h);
        state.flash *= 0.9;
      }

      const gust = Math.sin(t * 0.13) * 0.5 + Math.sin(t * 0.047) * 0.3;
      const { r, g, b } = pulseRgb(palette, t, colorMode === 'cycling' ? 0.4 : 0.06);

      for (const d of state.drops) {
        const wind = d.windBase + gust;
        const hx = d.x + wind * d.len * 0.4;
        const hy = d.y + d.len;
        const a = (dark ? 0.7 : 0.5) * (0.3 + 0.7 * d.depth);
        // Streak fades from faint tail to brighter head.
        const sg = ctx.createLinearGradient(d.x, d.y, hx, hy);
        sg.addColorStop(0, `rgba(${r},${g},${b},0)`);
        sg.addColorStop(1, `rgba(${r},${g},${b},${a})`);
        ctx.strokeStyle = sg;
        ctx.lineWidth = 0.7 + d.depth * 1.1;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(hx, hy);
        ctx.stroke();

        d.x += wind;
        d.y += d.speed;
        if (d.x < -20) d.x = w + 20;
        if (d.x > w + 20) d.x = -20;
        if (d.y > h) {
          if (state.splashes.length < 40 && d.depth > 0.45) {
            state.splashes.push({ x: d.x, y: h - 2, r: 0, life: 1, depth: d.depth });
          }
          d.y = -d.len;
          d.x = Math.random() * w;
        }
      }

      // Soft splash rings.
      state.splashes = state.splashes.filter(s => {
        s.r += 0.9;
        s.life -= 0.045;
        if (s.life <= 0) return false;
        ctx.strokeStyle = `rgba(${r},${g},${b},${s.life * (dark ? 0.55 : 0.4) * s.depth})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, Math.PI * 1.05, Math.PI * 1.95);
        ctx.stroke();
        return true;
      });
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
